import React, { memo, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import StyledTable from 'app/components/StyledTable';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import TextCell from 'app/pages/TicketRefund/components/TextCell';
import { Text, Title } from 'app/pages/TicketRefund/components/Typo';
import { formatNumber, formatTimeFrame, getFromDateToDate } from 'utils/helper';
import { renderTicketDetail } from '../../../TicketSelectedList';
import useScrollIntoElement from 'utils/hooks/useScrollIntoElement';

interface CancellationListProps {
  cancellationListData: any;
}

const CancellationList = (props: CancellationListProps) => {
  const { cancellationListData } = props;

  const translations = useSelector(selectTranslations);
  const anchorPoint = useRef<HTMLDivElement>(null);
  const { onScrollIntoElement } = useScrollIntoElement({
    elementRef: anchorPoint,
  });

  const columns = useMemo(
    () => [
      {
        title: (
          <span style={{ paddingLeft: 8 }}>
            {translations?.['b2b.txt.ticketName'] ?? ''}
          </span>
        ),
        dataIndex: 'ticketName',
        key: 'ticketName',
        width: 125,
      },
      {
        title: translations?.['b2b.txt.ticketPark'] ?? '',
        dataIndex: 'ticketPark',
        key: 'ticketPark',
        width: 200,
      },
      {
        title: translations?.['b2b.txt.ticketType'] ?? '',
        dataIndex: 'ticketType',
        key: 'ticketType',
        width: 100,
      },
      {
        title: translations?.['b2b.txt.ticket-usage-from'] ?? '',
        dataIndex: 'usageDate',
        key: 'usageDate',
        width: 116,
      },
      {
        title: translations?.['b2b.txt.ticket-usage-to'] ?? '',
        dataIndex: 'ticketExpired',
        key: 'ticketExpired',
        width: 116,
      },
      {
        title: translations?.['b2b.txt.ticket-detail'] ?? '',
        dataIndex: 'ticketDetail',
        key: 'ticketDetail',
        width: 103,
      },
      {
        title: (
          <span style={{ display: 'block', textAlign: 'right' }}>
            {translations?.['b2b.txt.ticketAmount'] ?? ''}
          </span>
        ),
        dataIndex: 'ticketAmount',
        key: 'ticketAmount',
        width: 100,
      },
      {
        title: (
          <span style={{ display: 'block', textAlign: 'right' }}>
            {translations?.['b2b.txt.unitPrice'] ?? ''}
          </span>
        ),
        dataIndex: 'unitPrice',
        key: 'unitPrice',
        width: 110,
      },
      {
        title: (
          <span style={{ display: 'block', textAlign: 'right' }}>
            {translations?.['b2b.txt.totalAmount'] ?? ''}
          </span>
        ),
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        width: 120,
      },
      {
        title: (
          <span style={{ display: 'block', textAlign: 'right' }}>
            {translations?.['b2b.txt.penaltyAccordingToPolicy'] ?? ''}
          </span>
        ),
        dataIndex: 'penaltyPolicy',
        key: 'penaltyPolicy',
        width: 130,
      },
      {
        title: (
          <span style={{ textAlign: 'right', display: 'block' }}>
            {translations?.['b2b.txt.fine-amount'] ?? ''}
          </span>
        ),
        dataIndex: 'fineAmount',
        key: 'fineAmount',
        width: 110,
      },
    ],
    [translations],
  );

  const dataSource = useMemo(() => {
    return cancellationListData?.map((ticket, idx) => {
      const { fromDate, toDate } = getFromDateToDate({
        catalog: { ...ticket?.catalog },
        date: { ...ticket?.dateFrom },
        show: { ...ticket?.show },
        product: { ...ticket?.product },
        ticket,
      });

      return {
        key: idx,
        ticketName: (
          <TextCell style={{ paddingLeft: 8 }}>
            {ticket?.product?.name}
          </TextCell>
        ),
        ticketPark: <TextCell>{ticket?.park?.name}</TextCell>,
        ticketType: <TextCell>{ticket?.catalog?.name}</TextCell>,
        usageDate: <TextCell>{fromDate}</TextCell>,
        ticketExpired: <TextCell>{toDate}</TextCell>,
        ticketDetail: (
          <TextCell>
            {renderTicketDetail([
              ticket?.product?.restaurant?.name,
              ticket?.ZoneName,
              formatTimeFrame(ticket?.product?.usedArea2Object?.label),
            ])}
          </TextCell>
        ),
        ticketAmount: (
          <TextCell style={{ textAlign: 'right' }}>{ticket?.amount}</TextCell>
        ),
        unitPrice: (
          <TextCell style={{ textAlign: 'right' }}>{`${formatNumber(
            ticket?.originalPrice ?? 0,
          ).replaceAll(',', '.')}đ`}</TextCell>
        ),
        totalAmount: (
          <TextCell style={{ textAlign: 'right' }}>{`${formatNumber(
            ticket?.totalPrice ?? 0,
          ).replaceAll(',', '.')}đ`}</TextCell>
        ),
        penaltyPolicy: (
          <TextCell
            style={{ textAlign: 'right' }}
          >{`${ticket?.percent}%`}</TextCell>
        ),
        fineAmount: (
          <TextCell style={{ textAlign: 'right' }}>{`${formatNumber(
            ticket?.penaltyFee ?? 0,
          ).replaceAll(',', '.')}đ`}</TextCell>
        ),
      };
    });
  }, [cancellationListData]);

  return (
    <div>
      <div>
        <Title style={{ textTransform: 'initial' }}>
          {translations?.['b2b.txt.ticketCancellationList'] ?? ''}
        </Title>
        <Text style={{ fontStyle: 'italic', margin: '16px 0px' }}>
          {translations?.['b2b.txt.cancellationNote'] ?? ''}
        </Text>
      </div>
      <div ref={anchorPoint} />
      <StyledTable
        columns={columns}
        dataSource={dataSource}
        styleSlots={{
          th: {
            fontWeight: 600,
            fontSize: 14,
            textAlign: 'left',
          },
        }}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: onScrollIntoElement,
        }}
      />
    </div>
  );
};

export default memo(CancellationList);
